import styled, { css, keyframes } from "styled-components";

import heroBg from "../../../assets/images/heroBg.webp";

/*
 * Početna, v2 redizajn (s.2): hero 80vh, bend traka (scroll-rail),
 * istaknuti proizvodi, value strip, novosti, newsletter.
 *
 * Animacije: hero se odigrava na load (CSS delays), ostale sekcije se
 * otkrivaju na scroll preko useReveal hooka ($visible prop).
 */

/* ------------------------------ animacije ------------------------------- */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(26px); }
  to { opacity: 1; transform: translateY(0); }
`;

const labelIn = keyframes`
  from { opacity: 0; letter-spacing: 0.45em; }
  to { opacity: 1; letter-spacing: 0.15em; }
`;

/* sprej se "iscrtava" sleva nadesno, clip se otkriva, oblik se ne razvlači */
const sprayReveal = keyframes`
  from { clip-path: inset(-25% 100.5% -25% -8%); }
  to { clip-path: inset(-25% -8% -25% -8%); }
`;

/* tamna boja teksta prati sprej front preko slova (background-clip: text) */
const inkSweep = keyframes`
  from { background-position: 100% 0; }
  to { background-position: 0% 0; }
`;

/* fallback bez background-clip: text, narandžast tekst potamni u jednom potezu */
const inkFlip = keyframes`
  from { color: var(--brand); }
  to { color: #131010; }
`;

/* kapljice sprej farbe oko natpisa, pojave se kad sprej pređe preko */
const sprayDotsIn = keyframes`
  from { opacity: 0; }
  to { opacity: 0.6; }
`;

const kenBurns = keyframes`
  from { transform: scale(1); }
  to { transform: scale(1.04); }
`;

const marqueeMove = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

/* Reveal na scroll, transition varijanta (za elemente bez hover transforma) */
const revealBase = css`
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? "0" : "26px")});
  transition:
    opacity 0.6s ease,
    transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

/* Stepenasti ulazak dece grida, animacija bez fill:forwards, pa posle
   ulaska važe bazni stilovi kartica (hover transform i sl. rade normalno) */
const staggerGrid = (count, step = 60) => css`
  & > * {
    opacity: 0;
  }

  ${(p) =>
    p.$visible &&
    css`
      & > * {
        opacity: 1;
        animation: ${fadeUp} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
      }

      ${Array.from(
        { length: count },
        (_, i) => css`
          & > *:nth-child(${i + 1}) {
            animation-delay: ${i * step}ms;
          }
        `,
      )}
    `}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      opacity: 1;
      animation: none;
    }
  }
`;

export const Wrap = styled.div`
  width: 100%;
`;

export const Container = styled.div`
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

/* --------------------------------- HERO --------------------------------- */
export const Hero = styled.section`
  position: relative;
  /* Visina prati odnos slike (1672x941 = 16:9) da fotografija stane cela,
     uz plafon da hero ne pređe viewport ispod headera (64px) */
  min-height: min(56.25vw, calc(100dvh - 64px));
  display: flex;
  align-items: center;
  overflow: hidden;

  /* pozadinska fotografija, spori Ken Burns zoom ka majicama */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url(${heroBg});
    background-size: cover;
    background-position: center;
    transform-origin: 70% 40%;
    animation: ${kenBurns} 26s ease-in-out infinite alternate;
    pointer-events: none;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  /* scrim, levo zatamnjenje za čitljivost teksta, gore/dole fade
     u boju pozadine da se hero stopi sa headerom i sledećom sekcijom */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(
        90deg,
        rgba(19, 16, 16, 0.92) 0%,
        rgba(19, 16, 16, 0.62) 40%,
        rgba(19, 16, 16, 0.12) 70%,
        rgba(19, 16, 16, 0) 100%
      ),
      linear-gradient(180deg, rgba(19, 16, 16, 0.5) 0%, rgba(19, 16, 16, 0) 22%),
      linear-gradient(0deg, #131010 0%, rgba(19, 16, 16, 0) 24%);
    pointer-events: none;
  }

  @media (max-width: 900px) {
    min-height: 70vh;

    &::before {
      background-position: 66% center;
    }

    /* na uskom ekranu tekst pokriva celu širinu, jači ravnomerni scrim */
    &::after {
      background:
        linear-gradient(
          90deg,
          rgba(19, 16, 16, 0.9) 0%,
          rgba(19, 16, 16, 0.7) 55%,
          rgba(19, 16, 16, 0.45) 100%
        ),
        linear-gradient(0deg, #131010 0%, rgba(19, 16, 16, 0) 28%);
    }
  }
`;

export const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 3rem 1.5rem;
  display: flex;
  align-items: center;

  @media (max-width: 900px) {
    padding: 2rem 1.5rem 3rem;
  }
`;

export const HeroContent = styled.div`
  max-width: 620px;
`;

export const HeroLabel = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 1rem;
  animation: ${labelIn} 0.9s cubic-bezier(0.2, 0.8, 0.2, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const HeroH1 = styled.h1`
  margin: 0 0 1.25rem;
  font-family: var(--font-display);
  font-size: clamp(40px, 6vw, 72px);
  line-height: 0.95;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
  animation: ${fadeUp} 0.65s cubic-bezier(0.2, 0.8, 0.2, 1) 0.1s both;

  em {
    position: relative;
    font-style: normal;
    /* fallback: narandžast → taman u jednom potezu */
    color: var(--brand);
    padding: 0 0.1em;
    animation: ${inkFlip} 0.15s ease 1s both;

    /* glavni efekat: narandžast tekst, pa ga sprej front "ofarba" u crno
       sleva nadesno. Gradijent je 220% širine elementa da tranziciona
       zona na oba kraja animacije završi VAN teksta (bez ostatka maske
       na poslednjem slovu) */
    @supports (-webkit-background-clip: text) {
      color: transparent;
      background-image: linear-gradient(90deg, #131010 48%, var(--brand) 52%);
      background-size: 220% 100%;
      background-position: 100% 0;
      -webkit-background-clip: text;
      background-clip: text;
      animation: ${inkSweep} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) 0.9s both;
    }

    /* narandžasti blok, kao sprejom prešarano preko naslova:
       nepravilan osnovni oblik + SVG displacement za grube ivice
       (filter #spray-edges je definisan u HomePage.js). Otkriva se
       clip-path animacijom, pa se tekstura ne razvlači, a i sama
       ivica otkrivanja je gruba jer filter ide posle clip-a */
    &::before {
      content: "";
      position: absolute;
      inset: -0.02em -0.14em -0.06em -0.12em;
      background: radial-gradient(
        120% 140% at 32% 45%,
        var(--brand) 55%,
        rgba(255, 107, 53, 0.82) 100%
      );
      border-radius: 0.12em 0.4em 0.18em 0.45em / 0.35em 0.16em 0.42em 0.2em;
      z-index: -1;
      filter: url(#spray-edges);
      transform: rotate(-1.3deg);
      clip-path: inset(-25% 100.5% -25% -8%);
      animation: ${sprayReveal} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) 0.9s both;
    }

    /* overspray, sitne kapljice rasute oko natpisa */
    &::after {
      content: "";
      position: absolute;
      inset: -0.4em -0.55em -0.45em -0.5em;
      z-index: -1;
      pointer-events: none;
      background-image: radial-gradient(
          circle 0.035em at 7% 16%,
          var(--brand) 98%,
          transparent
        ),
        radial-gradient(circle 0.02em at 15% 88%, var(--brand) 98%, transparent),
        radial-gradient(circle 0.028em at 3% 62%, var(--brand) 98%, transparent),
        radial-gradient(circle 0.018em at 38% 4%, var(--brand) 98%, transparent),
        radial-gradient(circle 0.03em at 62% 96%, var(--brand) 98%, transparent),
        radial-gradient(circle 0.02em at 78% 6%, var(--brand) 98%, transparent),
        radial-gradient(circle 0.045em at 93% 22%, var(--brand) 98%, transparent),
        radial-gradient(circle 0.025em at 97% 74%, var(--brand) 98%, transparent),
        radial-gradient(circle 0.035em at 88% 92%, var(--brand) 98%, transparent),
        radial-gradient(circle 0.015em at 55% 2%, var(--brand) 98%, transparent);
      animation: ${sprayDotsIn} 0.35s ease 1.05s both;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    em {
      animation: none;
      color: #131010;
      background-image: none;

      &::before {
        animation: none;
        clip-path: none;
      }

      &::after {
        animation: none;
        opacity: 0.6;
      }
    }
  }
`;

export const HeroSub = styled.p`
  margin: 0 0 2rem;
  max-width: 460px;
  color: var(--text-secondary);
  font-size: 18px;
  line-height: 1.6;
  animation: ${fadeUp} 0.65s cubic-bezier(0.2, 0.8, 0.2, 1) 0.25s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const HeroCtas = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  animation: ${fadeUp} 0.65s cubic-bezier(0.2, 0.8, 0.2, 1) 0.38s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 50px;
  padding: 0 1.75rem;
  background: var(--brand);
  color: #ffffff;
  border: none;
  border-radius: var(--radius-control);
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 0.18s ease,
    transform 0.18s ease;

  &:hover:not(:disabled) {
    background: var(--brand-hover);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 50px;
  padding: 0 1.5rem;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  svg {
    transition: transform 0.18s ease;
  }

  &:hover {
    border-color: var(--brand);
    color: var(--brand);

    svg {
      transform: translateX(3px);
    }
  }
`;

/* ------------------------------- sekcije -------------------------------- */
export const Section = styled.section`
  padding: 3.5rem 0;
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 1.75rem;
  ${revealBase}
`;

export const SectionLabel = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 0.6rem;
`;

export const SectionH2 = styled.h2`
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(28px, 4vw, 44px);
  line-height: 0.95;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
`;

export const SectionLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--brand);
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  padding-bottom: 0.35rem;

  svg {
    transition: transform 0.18s ease;
  }

  &:hover svg {
    transform: translateX(3px);
  }
`;

/* -------------------------- bend traka (marquee) -------------------------- */
export const BandRailCard = styled.div`
  position: relative;
  flex: 0 0 200px;
  height: 120px;
  /* margin umesto gap-a na traci: širina trake je tačno 2x polovina,
     pa translateX(-50%) pravi bešavnu petlju bez preskoka */
  margin-right: 1rem;
  border-radius: var(--radius-card);
  border: 1px solid var(--border);
  overflow: hidden;
  cursor: pointer;
  scroll-snap-align: start;
  background: var(--bg-elevated);
  transition:
    border-color 0.2s ease,
    transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1),
    opacity 0.25s ease,
    filter 0.25s ease;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      transparent 35%,
      rgba(19, 16, 16, 0.88) 100%
    );
  }

  &:hover {
    z-index: 2;
    border-color: var(--brand);
    transform: scale(1.05);
  }
`;

export const BandRailInfo = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  padding: 0.6rem 0.75rem;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;

  strong {
    font-family: var(--font-display);
    font-size: 0.95rem;
    text-transform: uppercase;
    letter-spacing: -0.01em;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    color: var(--text-secondary);
    font-size: 0.72rem;
    white-space: nowrap;
  }
`;

/* Traka koja se sama pomera, sadržaj je dupliran u JSX-u, petlja na -50% */
export const BandMarqueeTrack = styled.div`
  display: flex;
  width: max-content;
  will-change: transform;
  animation: ${marqueeMove} var(--marquee-duration, 45s) linear infinite;
`;

export const BandMarquee = styled.div`
  overflow: hidden;
  /* vertikalni prostor da scale(1.05) na hover ne bude odsečen */
  padding: 0.5rem 0 0.75rem;
  ${revealBase}

  /* fade sadržaja na levoj i desnoj ivici */
  mask-image: linear-gradient(
    to right,
    transparent,
    #000 5%,
    #000 95%,
    transparent
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    #000 5%,
    #000 95%,
    transparent
  );

  @media (hover: hover) {
    /* hover: traka staje, hoverovani bend u fokusu, ostali potamne */
    &:hover ${BandMarqueeTrack} {
      animation-play-state: paused;
    }

    &:hover ${BandRailCard} {
      opacity: 0.45;
      filter: saturate(0.35);
    }

    &:hover ${BandRailCard}:hover {
      opacity: 1;
      filter: none;
    }
  }

  /* Bez hovera (touch) ili uz reduced-motion: obična prevlačiva traka,
     klonovi sakriveni, bez animacije */
  @media (max-width: 768px), (prefers-reduced-motion: reduce) {
    overflow-x: auto;
    padding: 0.5rem 1.5rem 0.75rem;
    mask-image: none;
    -webkit-mask-image: none;
    scrollbar-width: thin;
    scrollbar-color: var(--brand) transparent;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 107, 53, 0.4);
      border-radius: 999px;
    }

    ${BandMarqueeTrack} {
      width: auto;
      animation: none;
    }

    [data-clone="true"] {
      display: none;
    }
  }
`;

/* --------------------------- proizvodi + novosti -------------------------- */
export const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  ${staggerGrid(8, 60)}

  @media (max-width: 1000px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

export const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  ${staggerGrid(3, 100)}

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

/* ------------------------------- value strip ----------------------------- */
export const ValueStrip = styled.div`
  background: var(--bg-surface);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
`;

export const ValueStripInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.9rem;
  }

  ${staggerGrid(3, 110)}
`;

export const ValueItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  color: var(--text-secondary);
  font-size: 0.92rem;

  svg {
    color: var(--brand);
    flex-shrink: 0;
  }
`;

/* -------------------------------- newsletter ----------------------------- */
export const NewsletterCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 2.5rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 2rem;
  align-items: center;

  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: ${(p) =>
    p.$visible ? "translateY(0) scale(1)" : "translateY(26px) scale(0.97)"};
  transition:
    opacity 0.65s ease,
    transform 0.65s cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 1.75rem;
  }
`;

export const NewsletterTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-family: var(--font-display);
  font-size: clamp(22px, 2.6vw, 30px);
  text-transform: uppercase;
  letter-spacing: -0.01em;
  line-height: 1;
  color: var(--text-primary);
`;

export const NewsletterSub = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.6;
`;

export const NewsletterForm = styled.form`
  display: flex;
  gap: 0.6rem;

  @media (max-width: 560px) {
    flex-direction: column;
  }
`;

export const NewsletterInput = styled.input`
  /* flex-basis mora biti auto, sa "flex: 1" (basis 0) input se u koloni
     na mobilnom sabije na ~0 visine uprkos height: 50px */
  flex: 1 1 auto;
  height: 50px;
  box-sizing: border-box;
  padding: 0 1rem;
  background: var(--bg-app);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.95rem;
  font-family: var(--font-body);
  transition: border-color 0.2s ease;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--brand);
  }
`;
